/**
 * Enhanced Toast Error Utilities
 * Provides better, more specific error messages in toast notifications
 */

import { toast } from '@/hooks/use-toast';
import { parseApiError } from './error-parser';

interface ToastErrorOptions {
  title?: string;
  showCode?: boolean;
  showFields?: boolean;
  showContext?: boolean;
  duration?: number;
}

/**
 * Show an error toast with detailed information
 */
export function showErrorToast(error: unknown, options: ToastErrorOptions = {}): void {
  const {
    title = 'Error',
    showCode = true,
    showFields = true,
    showContext = true,
    duration = 5000,
  } = options;

  const parsed = parseApiError(error);

  // Build description with all relevant details
  let description = parsed.message;

  // Add error code
  if (showCode && parsed.code) {
    description += ` [${parsed.code}]`;
  }

  // Add field errors
  if (showFields && parsed.fields && parsed.fields.length > 0) {
    const fieldMessages = parsed.fields.map(f => `${f.field}: ${f.message}`).join(', ');
    description += `\n\nFields: ${fieldMessages}`;
  }

  // Add context information
  if (showContext && parsed.context) {
    const contextInfo = Object.entries(parsed.context)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    if (contextInfo) {
      description += `\n\nDetails: ${contextInfo}`;
    }
  }

  toast({
    title,
    description,
    variant: 'destructive',
    duration,
  });
}

/**
 * Show a validation error toast with field-specific errors
 */
export function showValidationErrorToast(error: unknown): void {
  const parsed = parseApiError(error);

  if (parsed.fields && parsed.fields.length > 0) {
    const firstError = parsed.fields[0];
    const otherErrorsCount = parsed.fields.length - 1;

    let description = `${firstError.field}: ${firstError.message}`;

    if (otherErrorsCount > 0) {
      description += `\n\n+ ${otherErrorsCount} more error${otherErrorsCount > 1 ? 's' : ''}`;
    }

    toast({
      title: 'Validation Failed',
      description,
      variant: 'destructive',
      duration: 6000,
    });
  } else {
    showErrorToast(error, { title: 'Validation Failed' });
  }
}

/**
 * Show an authentication error toast
 */
export function showAuthErrorToast(error: unknown): void {
  const parsed = parseApiError(error);

  let title = 'Authentication Failed';
  let description = parsed.message;

  // Customize based on error code
  if (parsed.code === 'INVALID_CREDENTIALS') {
    title = 'Invalid Credentials';
    description = 'The email/username or password you entered is incorrect. Please try again.';
  } else if (parsed.code === 'EMAIL_NOT_VERIFIED') {
    title = 'Email Not Verified';
    description =
      'Please verify your email address before signing in. Check your inbox for the verification link.';
  } else if (parsed.code === 'TOKEN_EXPIRED') {
    title = 'Session Expired';
    description = 'Your session has expired. Please sign in again.';
  }

  toast({
    title,
    description,
    variant: 'destructive',
    duration: 5000,
  });
}

/**
 * Show a network/connection error toast
 */
export function showNetworkErrorToast(): void {
  toast({
    title: 'Connection Error',
    description:
      'Unable to connect to the server. Please check your internet connection and try again.',
    variant: 'destructive',
    duration: 5000,
  });
}

/**
 * Show a success toast (for convenience)
 */
export function showSuccessToast(message: string, title: string = 'Success'): void {
  toast({
    title,
    description: message,
    duration: 3000,
  });
}

/**
 * Get error toast options based on error type
 */
export function getErrorToastConfig(error: unknown): {
  title: string;
  description: string;
  variant: 'destructive';
} {
  const parsed = parseApiError(error);

  let title = 'Error';
  let description = parsed.message;

  // Customize title based on status code or error code
  if (parsed.statusCode === 401 || parsed.code === 'UNAUTHORIZED') {
    title = 'Authentication Required';
  } else if (parsed.statusCode === 403 || parsed.code === 'FORBIDDEN') {
    title = 'Permission Denied';
  } else if (parsed.statusCode === 404 || parsed.code === 'NOT_FOUND') {
    title = 'Not Found';
  } else if (parsed.statusCode === 409 || parsed.code === 'ALREADY_EXISTS') {
    title = 'Already Exists';
  } else if (parsed.code === 'VALIDATION_ERROR') {
    title = 'Validation Failed';
  } else if (parsed.code === 'INSUFFICIENT_CREDITS') {
    title = 'Insufficient Credits';
  } else if (parsed.statusCode && parsed.statusCode >= 500) {
    title = 'Server Error';
  }

  // Add field errors to description
  if (parsed.fields && parsed.fields.length > 0) {
    const fieldMessages = parsed.fields.map(f => `${f.field}: ${f.message}`).join(', ');
    description += `\n${fieldMessages}`;
  }

  // Add context
  if (parsed.context) {
    const contextInfo = Object.entries(parsed.context)
      .filter(([key]) => !['totalErrors'].includes(key))
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    if (contextInfo) {
      description += `\n${contextInfo}`;
    }
  }

  return {
    title,
    description,
    variant: 'destructive',
  };
}
